// Página CustomerDetail - Detalle completo de un cliente
// Muestra información del cliente y su historial de pedidos

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getCustomerByEmail, getCustomerOrders } from '../../services/customers.service'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import './CustomerDetail.css'

/**
 * Página CustomerDetail
 * Muestra el detalle completo de un cliente
 */
function CustomerDetail() {
  const { email } = useParams()
  const navigate = useNavigate()
  const decodedEmail = decodeURIComponent(email)
  
  const [customer, setCustomer] = useState(null)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchCustomerData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Obtener información del cliente y sus pedidos en paralelo
      const [customerData, ordersData] = await Promise.all([
        getCustomerByEmail(decodedEmail),
        getCustomerOrders(decodedEmail)
      ])
      
      if (!customerData) {
        setError('Cliente no encontrado')
        return
      }
      
      setCustomer(customerData)
      setOrders(ordersData)
    } catch (err) {
      console.error('Error cargando cliente:', err)
      setError('Error al cargar la información del cliente')
    } finally {
      setLoading(false)
    }
  }, [decodedEmail])

  useEffect(() => {
    fetchCustomerData()
  }, [fetchCustomerData])

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copiado al portapapeles:', text)
    })
  }

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'En Proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  if (loading) {
    return (
      <div className="customer-detail-page">
        <div className="loading-state">
          <p>Cargando información del cliente...</p>
        </div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="customer-detail-page">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error || 'No se pudo cargar la información del cliente'}</p>
          <Button onClick={() => navigate('/admin/clientes')}>
            Volver a Clientes
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="customer-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div>
          <Link to="/admin/clientes" className="back-link">
            ← Volver a Clientes
          </Link>
          <h1>{customer.name}</h1>
          <p className="detail-subtitle">{customer.email}</p>
        </div>
        <div className="header-actions">
          <button
            className="action-btn"
            onClick={() => copyToClipboard(customer.email)}
            title="Copiar email"
          >
            📧 Copiar Email
          </button>
          {customer.phone && (
            <button
              className="action-btn"
              onClick={() => copyToClipboard(customer.phone)}
              title="Copiar teléfono"
            >
              📋 Copiar Teléfono
            </button>
          )}
        </div>
      </div>

      <div className="detail-content">
        {/* Información del cliente */}
        <div className="detail-section">
          <h2 className="section-title">Información del Cliente</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nombre:</label>
              <span>{customer.name}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span className="monospace">{customer.email}</span>
            </div>
            <div className="info-item">
              <label>Teléfono:</label>
              <span>{customer.phone || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Total de Pedidos:</label>
              <span className="highlight">{customer.totalOrders}</span>
            </div>
            <div className="info-item">
              <label>Total Gastado:</label>
              <span className="highlight revenue">{formatCurrency(customer.totalSpent)}</span>
            </div>
            <div className="info-item">
              <label>Ticket Promedio:</label>
              <span className="highlight">
                {formatCurrency(Math.round(customer.totalSpent / customer.totalOrders))}
              </span>
            </div>
            <div className="info-item">
              <label>Primer Pedido:</label>
              <span>
                {customer.firstOrderDate ? formatDate(customer.firstOrderDate) : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <label>Último Pedido:</label>
              <span>
                {customer.lastOrderDate ? formatDate(customer.lastOrderDate) : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Direcciones */}
        {customer.addresses.length > 0 && (
          <div className="detail-section">
            <h2 className="section-title">Direcciones de Envío</h2>
            <div className="addresses-grid">
              {customer.addresses.map((address, index) => (
                <div key={index} className="address-card">
                  <p className="address-street">{address.street}</p>
                  <p className="address-city">
                    {address.city}
                    {address.postalCode && ` (${address.postalCode})`}
                  </p>
                  <p className="address-province">{address.province}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Historial de pedidos */}
        <div className="detail-section">
          <h2 className="section-title">Historial de Pedidos ({orders.length})</h2>
          {orders.length === 0 ? (
            <p className="empty-message">Este cliente no tiene pedidos aún.</p>
          ) : (
            <div className="orders-table-container">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const date = order.createdAt?.toDate 
                      ? formatDate(order.createdAt.toDate())
                      : formatDate(new Date())

                    return (
                      <tr key={order.id}>
                        <td>
                          <Link 
                            to={`/admin/pedidos/${order.id}`}
                            className="order-number-link"
                          >
                            {order.orderNumber || order.id}
                          </Link>
                        </td>
                        <td>{date}</td>
                        <td>
                          <span className={getStatusClass(order.status)}>
                            {getStatusLabel(order.status)}
                          </span>
                        </td>
                        <td className="total-cell">{formatCurrency(order.total || 0)}</td>
                        <td>
                          <Link to={`/admin/pedidos/${order.id}`}>
                            <Button variant="outline" size="small">
                              Ver
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomerDetail

